import { useState } from "react";
import {
  Alert,
  Button,
  DataTable,
  Icon,
  InputText,
  ListScreen,
  RigoFilters,
  Select,
  Tooltip,
  type ColumnDef,
  type SelectOption,
} from "@/deps/ui-library";
import {
  useAccounts,
  useBillingItems,
  useDeleteBillingItem,
  useSaveBillingItem,
} from "@/api/queries";
import type { Account, BillingItem, BillingItemForm, VatCategory } from "@/api/types";
import { useCan, useTaxWords } from "@/app/session";
import { Dialog } from "@/components/Dialog";
import { FilterChip } from "@/components/list/ListFilterBar";
import { PermittedAction } from "@/components/PermittedAction";
import { SegmentControl } from "@/components/SegmentControl";
import { formatAmount } from "@/lib/money";
import { sellingAccounts } from "@/lib/sellingAccounts";

const STANDARD_RATE = "13";

const CATEGORY_LABEL: Record<VatCategory, string> = {
  S: "VAT 13%",
  Z: "Zero-rated",
  E: "Exempt",
  AE: "Reverse charge",
  O: "Outside scope",
};

const NON_TAXABLE: VatCategory[] = ["Z", "E", "AE", "O"];

/** Zero-rated is the one category besides standard with no reason on the document. */
const hasReason = (category: VatCategory) => category !== "S" && category !== "Z";

function chosen(details: { value: SelectOption | SelectOption[] | null }): string {
  const option = Array.isArray(details.value) ? details.value[0] : details.value;
  return option ? String(option.value) : "";
}

/**
 * What a company bills, with the price, tax and income account each puts on an invoice line.
 *
 * An item only fills in a line. The line keeps its own copy, so editing an item changes future
 * invoices and not issued ones, and an item on any document can't be deleted.
 */
export function BillingItemsPage() {
  const say = useTaxWords();
  const can = useCan();
  const [q, setQ] = useState("");
  const [accountId, setAccountId] = useState("");
  const [category, setCategory] = useState("");
  const [editing, setEditing] = useState<BillingItem | "new" | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const { data: chart } = useAccounts();
  const income = sellingAccounts(chart);
  const {
    data: items,
    isPending,
    error,
  } = useBillingItems({
    q,
    accountId: accountId || undefined,
    category: (category || undefined) as VatCategory | undefined,
  });
  const remove = useDeleteBillingItem();
  const manages = can("settings.parties.manage");

  const accountLabel = (id: string) => {
    const account = chart?.find((a) => a.id === id);
    return account ? `${account.code} · ${account.name}` : "";
  };

  const columns: Array<ColumnDef<BillingItem>> = [
    {
      id: "item",
      header: "Item",
      cell: ({ row: { original: item } }) => (
        <span className="flex flex-col">
          <span className="text-fg text-sm">{item.name}</span>
          {item.description ? (
            <span className="text-fg-muted text-xs">{item.description}</span>
          ) : null}
        </span>
      ),
    },
    {
      id: "hs",
      header: "HS / SAC",
      cell: ({ row: { original: item } }) => item.hsCode,
    },
    {
      id: "account",
      header: "Default account",
      cell: ({ row: { original: item } }) => accountLabel(item.incomeAccountId),
    },
    {
      id: "tax",
      header: "Tax",
      cell: ({ row: { original: item } }) => say(CATEGORY_LABEL[item.category]),
    },
    {
      id: "price",
      header: "Unit price",
      meta: { textAlign: "right", numeric: true },
      cell: ({ row: { original: item } }) => formatAmount(item.unitPrice),
    },
    ...(manages
      ? [
          {
            id: "actions",
            header: "",
            cell: ({ row: { original: item } }: { row: { original: BillingItem } }) => {
              const used = item.usedOnDocuments > 0;
              // Refused, not inert: `aria-disabled` keeps it focusable, so the tooltip that says
              // why can open. A `disabled` button takes no pointer or focus, and its reason is lost.
              const refusal = used
                ? `Used on ${item.usedOnDocuments} ${item.usedOnDocuments === 1 ? "document" : "documents"}, so it can't be deleted`
                : null;
              return (
                <span className="flex justify-end gap-1">
                  <Tooltip content="Edit item">
                    <Button.IconButton
                      icon="Pencil"
                      iconVariant="cell"
                      variant="secondary"
                      size="xs"
                      aria-label={`Edit ${item.name}`}
                      onClick={() => setEditing(item)}
                    />
                  </Tooltip>
                  <Tooltip content={refusal ?? "Delete item"}>
                    <Button.IconButton
                      icon="Trash2"
                      iconVariant="cell"
                      variant="secondary"
                      size="xs"
                      aria-disabled={used || remove.isPending ? "true" : undefined}
                      aria-label={`Delete ${item.name}`}
                      onClick={() => {
                        if (used || remove.isPending) return;
                        setRefused(null);
                        remove.mutate(item.id, {
                          onError: (failure) => setRefused((failure as Error).message),
                        });
                      }}
                    />
                  </Tooltip>
                </span>
              );
            },
          },
        ]
      : []),
  ];

  const filtered = q !== "" || accountId !== "" || category !== "";

  return (
    <>
      <ListScreen
        // The shell's main has the padding.
        className="p-0"
        title="Billing items"
        description={`${items?.length ?? 0} items · price, tax treatment and account filled in on an invoice line`}
        actions={
          <PermittedAction
            permission="settings.parties.manage"
            leftIcon="Plus"
            onClick={() => setEditing("new")}
          >
            Add item
          </PermittedAction>
        }
        filters={
          <div className="flex flex-col gap-3">
            {refused ? (
              <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={refused} />
            ) : null}
            <RigoFilters.Root>
              <RigoFilters.StaticRow>
                <div className="w-60">
                  <InputText.FilterSearch
                    aria-label="Search items"
                    placeholder="Search name, code or description"
                    value={q}
                    onChange={(event) => setQ(event.target.value)}
                    onClear={() => setQ("")}
                  />
                </div>
                <FilterChip
                  placeholder="Account"
                  value={accountId || undefined}
                  options={income.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }))}
                  onChange={(value) => setAccountId(value ?? "")}
                />
                <FilterChip
                  placeholder="Tax treatment"
                  value={category || undefined}
                  options={(["S", ...NON_TAXABLE] as VatCategory[]).map((value) => ({
                    value,
                    label: say(CATEGORY_LABEL[value]),
                  }))}
                  onChange={(value) => setCategory(value ?? "")}
                />
              </RigoFilters.StaticRow>
            </RigoFilters.Root>
          </div>
        }
        loading={isPending}
        error={error}
        empty={(items?.length ?? 0) === 0}
        filtered={filtered}
        onClearFilters={() => {
          setQ("");
          setAccountId("");
          setCategory("");
        }}
        emptyState={{
          title: "No billing items yet",
          description: "Add one to fill in an invoice line's price, tax and account.",
          icon: <Icon name="Package" size="lg" color="inherit" />,
        }}
        filteredEmptyState={{
          title: "No items match",
          description: "Try a wider search, or clear the filters.",
        }}
      >
        <DataTable
          aria-label="Billing items"
          size="sm"
          enableSorting={false}
          columns={columns}
          data={items ?? []}
          getRowId={(item) => item.id}
          pagination={{ mode: "manual", show: false }}
        />
      </ListScreen>

      {editing ? (
        <ItemDialog
          item={editing === "new" ? undefined : editing}
          accounts={income}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function ItemDialog({
  item,
  accounts,
  onClose,
}: {
  item: BillingItem | undefined;
  accounts: Account[];
  onClose: () => void;
}) {
  const say = useTaxWords();
  const save = useSaveBillingItem();
  const [name, setName] = useState(item?.name ?? "");
  const [price, setPrice] = useState(item?.unitPrice ?? "");
  const [hsCode, setHsCode] = useState(item?.hsCode ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [account, setAccount] = useState(item?.incomeAccountId ?? "");
  const [taxable, setTaxable] = useState((item?.category ?? "S") === "S");
  const [nonTaxable, setNonTaxable] = useState<VatCategory>(
    item && item.category !== "S" ? item.category : "Z",
  );
  const [reason, setReason] = useState(item?.exemptionReason ?? "");

  const incomeAccountId = account || accounts[0]?.id || "";
  const category: VatCategory = taxable ? "S" : nonTaxable;
  const reasonShown = hasReason(category);
  const priceOk = /^\d+(\.\d{1,6})?$/.test(price.replaceAll(",", "").trim());
  const canSave = name.trim() !== "" && priceOk && incomeAccountId !== "" && !save.isPending;

  const form = (): BillingItemForm => ({
    name: name.trim(),
    description: description.trim(),
    hsCode: hsCode.trim(),
    unitPrice: price.replaceAll(",", "").trim(),
    incomeAccountId,
    category,
    ratePct: taxable ? STANDARD_RATE : null,
    exemptionReason: reasonShown ? reason.trim() : "",
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={item ? "Edit billing item" : "New billing item"}
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={!canSave}
          onClick={() => save.mutate({ id: item?.id, form: form() }, { onSuccess: onClose })}
        >
          {save.isPending ? "Saving" : "Save item"}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {save.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(save.error as Error).message}
          />
        ) : null}
        <InputText
          label="Item or service name"
          required
          placeholder="e.g. Consulting engagement"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <div className="grid gap-3 md:grid-cols-2">
          <InputText
            label="Unit price"
            required
            inputMode="decimal"
            placeholder="0.00"
            className="num"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
          />
          <InputText
            label="HS / SAC code · optional"
            placeholder="e.g. SAC 9983"
            value={hsCode}
            onChange={(event) => setHsCode(event.target.value)}
          />
        </div>
        <InputText
          label="Description · shown with the item name on the invoice"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Select
          label="Default account"
          required
          options={accounts.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }))}
          value={incomeAccountId}
          onChange={(details) => setAccount(chosen(details))}
          showClearButton={false}
        />
        <SegmentControl<"taxable" | "none">
          label="Tax treatment"
          segments={[
            { value: "taxable", label: "Taxable" },
            { value: "none", label: "Not taxable" },
          ]}
          value={taxable ? "taxable" : "none"}
          onChange={(next) => setTaxable(next === "taxable")}
        />
        {taxable ? (
          <p className="text-fg-muted text-xs">{say(`Standard rate · VAT ${STANDARD_RATE}%`)}</p>
        ) : (
          <Select
            label={say("Default VAT category")}
            options={NON_TAXABLE.map((value) => ({ value, label: say(CATEGORY_LABEL[value]) }))}
            value={nonTaxable}
            onChange={(details) => {
              const picked = chosen(details);
              if (picked) setNonTaxable(picked as VatCategory);
            }}
            showClearButton={false}
          />
        )}
        {reasonShown ? (
          <InputText
            label="Exemption reason · optional, goes on an invoice using this item"
            placeholder="e.g. Educational services · Schedule 1"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        ) : null}
      </div>
    </Dialog>
  );
}
